/*
 * Storefront product visibility (Productive #210).
 *
 * One shared check for "can the shopper's storefront see this product?", used by the
 * Liquid card swatches (product-card-sibling-check.js) and the Tagalys search cards
 * (tagalys-product-card.js). Colour swatches come from the siblings metafield or the
 * `x-sibling-handle:` tags; the tags list archived and unpublished colourways, and the
 * metafield's `published` flag drifts from the Online Store, so each sibling handle is
 * checked against the Storefront API: `product(handle:)` is null for a product the
 * storefront cannot see in the shopper's market.
 *
 * Exposes window.theme.storefrontVisibility:
 *   verify(handles) - Promise, resolves once every handle is checked (or has failed);
 *   isOffline(handle) / isOnline(handle) - true only after a clean answer.
 *
 * Fails open: a network error, a non-OK response or a GraphQL `errors` payload leaves
 * the handle unknown, so callers keep the swatch. The first failure is logged once so
 * a broken check is visible rather than silent. A failed handle is retried once.
 */
(() => {
	"use strict";

	const BATCH = 50;
	const MAX_ATTEMPTS = 2;
	const status = Object.create(null); /* handle -> "online" | "offline" */
	const inflight = Object.create(null); /* handle -> Promise */
	const attempts = Object.create(null); /* handle -> failed attempts */
	let warned = false;

	/* Token and version come from window.theme.storefrontApi (layout/theme.liquid,
	   Theme settings > Storefront API) - the one place the API version defaults. */
	function apiConfig() {
		return (window.theme && window.theme.storefrontApi) || {};
	}

	function endpoint() {
		return `/api/${apiConfig().apiVersion}/graphql.json`;
	}

	function warnOnce(reason) {
		if (warned) return;
		warned = true;
		console.warn(
			`[storefront-visibility] Product check failed (${reason}); colour swatches for offline products may show.`
		);
	}

	function shopperCountry() {
		const country =
			(window.Shopify && window.Shopify.country) || (window.theme && window.theme.countryCode);
		return /^[A-Z]{2}$/.test(country) ? country : "";
	}

	function request(batch) {
		const { token } = apiConfig();
		const headers = { "Content-Type": "application/json" };
		if (token) headers["X-Shopify-Storefront-Access-Token"] = token;

		/* Without a country context the API answers for a wider scope than the shopper's
		   market, so a product excluded from the AU catalogue (gordy-honey-scale) read as
		   online while its page 404s. */
		const country = shopperCountry();
		const aliases = batch
			.map((handle, index) => `s${index}: product(handle: ${JSON.stringify(handle)}) { id }`)
			.join("\n");
		const query = `${country ? `query @inContext(country: ${country}) ` : ""}{\n${aliases}\n}`;

		const fail = (reason) => {
			warnOnce(reason);
			batch.forEach((handle) => {
				attempts[handle] = (attempts[handle] || 0) + 1;
			});
		};

		return window
			.fetch(endpoint(), { method: "POST", headers, body: JSON.stringify({ query }) })
			.then((response) => {
				if (!response.ok) {
					fail(`HTTP ${response.status}`);
					return;
				}
				return response.json().then((payload) => {
					/* Only a clean response is trusted: alongside errors (throttling) a null
					   alias does not mean the product is offline. */
					if (!payload || !payload.data || payload.errors) {
						fail("GraphQL errors");
						return;
					}
					batch.forEach((handle, index) => {
						status[handle] = payload.data[`s${index}`] === null ? "offline" : "online";
					});
				});
			})
			.catch(() => fail("network error"));
	}

	function verify(handles) {
		const unique = [...new Set((handles || []).filter(Boolean).map(String))];
		if (typeof window.fetch !== "function" || !apiConfig().apiVersion) {
			return Promise.resolve();
		}

		const pending = unique.filter(
			(handle) =>
				!(handle in status) && !(handle in inflight) && (attempts[handle] || 0) < MAX_ATTEMPTS
		);
		for (let start = 0; start < pending.length; start += BATCH) {
			const batch = pending.slice(start, start + BATCH);
			const done = request(batch).finally(() => {
				batch.forEach((handle) => delete inflight[handle]);
			});
			batch.forEach((handle) => {
				inflight[handle] = done;
			});
		}

		return Promise.all(unique.map((handle) => inflight[handle])).then(() => undefined);
	}

	window.theme = window.theme || {};
	window.theme.storefrontVisibility = {
		verify,
		endpoint,
		isOffline: (handle) => status[handle] === "offline",
		isOnline: (handle) => status[handle] === "online"
	};
})();
